import { FormHelperText, FormLabel, Grid, TextField } from '@mui/material';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './IpField.scss';
import { Regex } from '@utils/constants';

const AppIpField: FC<AppIpFieldProps> = ({
  isRow = false,
  id,
  label,
  required,
  disabled,
  value,
  defautValue,
  onChange,
  error,
}) => {
  const { t } = useTranslation();

  const [values, setValues] = useState(['', '', '', '']);

  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const errorText = useMemo(() => {
    if (error?.type === 'required' && !error.message) {
      return t('common.require_input_text');
    }

    return error?.message ? t(error?.message) : null;
  }, [error]);

  useEffect(() => {
    if (defautValue) {
      const values = ['', '', '', ''];
      defautValue.split('.').forEach((dfValue, idx) => (values[idx] = dfValue));
      setValues(values);
    }
  }, []);

  useEffect(() => {
    if (value) {
      const values = ['', '', '', ''];
      value.split('.').forEach((dfValue, idx) => (values[idx] = dfValue));
      setValues(values);
    }
  }, [value]);

  const convertValues = (values: string[]) => {
    let index = 0;
    for (let j = 0; j < 4; j++) {
      if (values[j]) index = j;
    }
    for (let j = 0; j < index; j++) {
      if (!values[j]) values[j] = '0';
    }
    return [...values];
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const index = Number(event.target.id);
    const value = event.target.value.slice(0, 3);

    if (value === '') {
      values[index] = '';
    } else if (Number(value) > 255) {
      values[index] = '255';
    } else {
      values[index] = String(Number(value));
    }

    if (value.length === 3) {
      inputRefs.current[index + 1]?.focus();
    }

    const newValues = convertValues(values);
    setValues(newValues);

    const returnValue = newValues.filter(value => !!value).join('.');
    if (required) {
      onChange?.(Regex.RULE.IP.test(returnValue) ? returnValue : '');
    } else {
      onChange?.(returnValue);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (['e', 'E', '-', '.', ','].includes(event.key)) {
      event.preventDefault();
    }

    const index = Number(event.currentTarget.id);
    if (event.key === '.') {
      inputRefs.current[index + 1]?.focus();
    } else if (event.key === 'Backspace' && !event.currentTarget.value.length) {
      event.preventDefault();
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (event: React.ClipboardEvent) => {
    event.preventDefault();
    const paste = event.clipboardData.getData('text');
    if (Regex.RULE.IP.test(paste)) {
      const parts = paste.split('.');
      const newValues = parts.map(part => {
        const num = Number(part);
        return num > 255 ? '255' : String(num);
      });
      setValues(newValues);
      onChange?.(newValues.join('.'));

      inputRefs.current[3]?.focus();
    }
  };

  return (
    <Grid
      container
      className='AppIpField'
      direction={isRow ? 'row' : 'column'}
      spacing={isRow ? 3 : 2}
    >
      {label && (
        <Grid
          item
          xs={4}
          alignItems='center'
          justifyContent={isRow ? 'flex-end' : 'flex-start'}
        >
          <FormLabel htmlFor={id} required={required}>
            {t(label)}
          </FormLabel>
        </Grid>
      )}
      <Grid item xs={8} sx={{ width: '100%' }}>
        <div className='FieldWrapper'>
          <div className='FieldWrapper__content' onPaste={handlePaste}>
            {values.map((value, index) => (
              <TextField
                id={String(index)}
                key={index}
                disabled={disabled}
                type='number'
                variant='standard'
                value={value}
                InputProps={{ disableUnderline: true }}
                inputProps={{
                  min: 0,
                  ref: (el: HTMLInputElement | null) =>
                    (inputRefs.current[index] = el),
                  onKeyDown: handleKeyDown,
                  onChange: handleChange,
                }}
                error={!!errorText}
              />
            ))}
          </div>
          <input
            id={id}
            name={id}
            readOnly
            aria-hidden
            disabled={disabled}
            className='FieldWrapper__input'
            tabIndex={-1}
            value={values.filter(value => !!value).join('.')}
          />
          {errorText && <FormHelperText error>{errorText}</FormHelperText>}
        </div>
      </Grid>
    </Grid>
  );
};

export default AppIpField;
